// tslint:disable:max-line-length
import { TCI_MARKETPLACE_LISTING_VIEW_MODE } from "@tci/common";
import { Decision, BoolTable, Truth } from "booltable";
import { DecisionMonad, BoolTableMonad } from "booltable/dist/types";
import {
    CONDITION_IS_ALLOWED_IN,
    CONDITION_STATUS_MANAGING,
    MKP_PUBLISH_ITEMS,
    IMkpV2ListContext,
    IMkpV2ListEvalRes,
    formatResult,
    isAdminOfSameOrg,
    ConditionsForStatusManaging
} from "./listing-bt-evaluations";

//#region - Conditions catalog binding condition string with the actual evaluation
const ConditionsForAllowed = (context: IMkpV2ListContext): BoolTableMonad =>
    BoolTable.of([
        [
            CONDITION_IS_ALLOWED_IN.LIST_CARD,
            Truth.of([
                [
                    MKP_PUBLISH_ITEMS.PUBLIC,
                    MKP_PUBLISH_ITEMS.PENDING_APPROVAL,
                    MKP_PUBLISH_ITEMS.ACCESS_REQUEST_COUNT,
                    MKP_PUBLISH_ITEMS.PRIVATE
                ].map(item => item.toString()).includes(context.item.toString())
            ]).and()
        ],
        [
            CONDITION_IS_ALLOWED_IN.DETAIL,
            Truth.of([
                [
                    MKP_PUBLISH_ITEMS.PUBLIC,
                    MKP_PUBLISH_ITEMS.PENDING_APPROVAL,
                    MKP_PUBLISH_ITEMS.ACCESS_REQUEST_COUNT,
                    MKP_PUBLISH_ITEMS.PRIVATE
                ].map(item => item.toString()).includes(context.item.toString())
            ]).and()
        ],
        [
            CONDITION_IS_ALLOWED_IN.PREVIEW_CARD,
            Truth.of([
                [
                    MKP_PUBLISH_ITEMS.PENDING_APPROVAL
                ].map(item => item.toString()).includes(context.item.toString())
            ]).and()
        ],
        [
            CONDITION_IS_ALLOWED_IN.PREVIEW_DETAIL,
            Truth.of([
                [
                    MKP_PUBLISH_ITEMS.PENDING_APPROVAL
                ].map(item => item.toString()).includes(context.item.toString())
            ]).and()
        ]
    ]);
//#endregion

// Use this function to see if an item is allowed in a certain mode
const CommonAllowanceCheck = (context: IMkpV2ListContext): IMkpV2ListEvalRes => {
    let condTable: DecisionMonad;
    switch (context.mode) {
        case TCI_MARKETPLACE_LISTING_VIEW_MODE.LIST_CARD:
            condTable = Decision.of([
                [ConditionsForAllowed(context).q(CONDITION_IS_ALLOWED_IN.LIST_CARD), { visible: true }]
            ]);
            break;
        case TCI_MARKETPLACE_LISTING_VIEW_MODE.LIST_DETAIL:
            condTable = Decision.of([
                [ConditionsForAllowed(context).q(CONDITION_IS_ALLOWED_IN.DETAIL), { visible: true }]
            ]);
            break;
        case TCI_MARKETPLACE_LISTING_VIEW_MODE.PREVIEW_CARD:
            condTable = Decision.of([
                [ConditionsForAllowed(context).q(CONDITION_IS_ALLOWED_IN.PREVIEW_CARD), { visible: true }]
            ]);
            break;
        case TCI_MARKETPLACE_LISTING_VIEW_MODE.PREVIEW_DETAIL:
            condTable = Decision.of([
                [ConditionsForAllowed(context).q(CONDITION_IS_ALLOWED_IN.PREVIEW_DETAIL), { visible: true }]
            ]);
            break;
    }

    return condTable.run("any").chain(formatResult);
};

// Use this function to see if context is in preview mode
const isPreviewMode = (context: IMkpV2ListContext): boolean => {
    return [
        TCI_MARKETPLACE_LISTING_VIEW_MODE.PREVIEW_CARD,
        TCI_MARKETPLACE_LISTING_VIEW_MODE.PREVIEW_DETAIL
    ].includes(context.mode as any);
};

export const CheckPublic = (context: IMkpV2ListContext, allowanceCheck = CommonAllowanceCheck): IMkpV2ListEvalRes => {
    const allowanceResult = allowanceCheck(context);
    allowanceResult.visible = allowanceResult.visible && !isPreviewMode(context);

    // If visible by mode then check by status managing
    if (allowanceResult.visible) {
        const condTable = Decision.of([
            [ConditionsForStatusManaging(context).q(CONDITION_STATUS_MANAGING.IS_PUBLICLY_AVAILABLE), { visible: true }]
        ]);
        return condTable.run("any").chain(formatResult);
    }
    return allowanceResult;
};

export const CheckPrivate = (context: IMkpV2ListContext, allowanceCheck = CommonAllowanceCheck): IMkpV2ListEvalRes => {
    const allowanceResult = allowanceCheck(context);
    allowanceResult.visible = allowanceResult.visible && !isPreviewMode(context);

    // If visible by mode then check by status managing
    if (allowanceResult.visible) {
        const condTable: DecisionMonad = Decision.of([
            [ConditionsForStatusManaging(context).q(CONDITION_STATUS_MANAGING.IS_PRIVATELY_AVAILABLE), { visible: true }]
        ]);
        return condTable.run("any").chain(formatResult);
    }
    return allowanceResult;
};

export const CheckPendingApproval = (context: IMkpV2ListContext, allowanceCheck = CommonAllowanceCheck): IMkpV2ListEvalRes => {
    const allowanceResult = allowanceCheck(context);
    allowanceResult.visible = allowanceResult.visible && !isPreviewMode(context);

    // If visible by mode then check by status managing
    if (allowanceResult.visible) {
        const condTable = Decision.of([
            [ConditionsForStatusManaging(context).q(CONDITION_STATUS_MANAGING.IS_PENDING_APPROVAL), { visible: true }]
        ]);
        return condTable.run("any").chain(formatResult);
    }
    return allowanceResult;
};

export const CheckAccessRequestCount = (context: IMkpV2ListContext, allowanceCheck = CommonAllowanceCheck): IMkpV2ListEvalRes => {
    const allowanceResult = allowanceCheck(context);

    // PREVIEW modes skip all status evaluations and the element is not visible
    allowanceResult.visible = !isPreviewMode(context) &&
        isAdminOfSameOrg(context) &&
        !isNaN(context.asset.numPendingAccessRequests) &&
        context.asset.numPendingAccessRequests > 0;

    // If visible by mode then check by status managing
    if (allowanceResult.visible) {
        const condTable = Decision.of([
            [ConditionsForStatusManaging(context).q(CONDITION_STATUS_MANAGING.IS_PUBLICLY_AVAILABLE), { visible: true }],
            [ConditionsForStatusManaging(context).q(CONDITION_STATUS_MANAGING.IS_PRIVATELY_AVAILABLE), { visible: true }],
            [ConditionsForStatusManaging(context).q(CONDITION_STATUS_MANAGING.IS_PENDING_APPROVAL), { visible: true }],
        ]);
        return condTable.run("any").chain(formatResult);
    }
    return allowanceResult;
};

export const CheckOverPublishItem = (context: IMkpV2ListContext): IMkpV2ListEvalRes => {
    switch (context.item) {
        case MKP_PUBLISH_ITEMS.PUBLIC:
            return CheckPublic(context);
        case MKP_PUBLISH_ITEMS.PENDING_APPROVAL:
            return CheckPendingApproval(context);
        case MKP_PUBLISH_ITEMS.ACCESS_REQUEST_COUNT:
            return CheckAccessRequestCount(context);
        case MKP_PUBLISH_ITEMS.PRIVATE:
            return CheckPrivate(context);
    }
};
